import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SEOMeta from '@/components/feature/SEOMeta';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-900">文章未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categoryMap[article.category];
  const relatedArticles = getRelatedArticles(article, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    url: `https://buliangyanjiusuo.com/article/${article.slug}`,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
      description: article.authorBio,
    },
    publisher: {
      '@type': 'Organization',
      name: '不良研究所',
      url: 'https://buliangyanjiusuo.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://buliangyanjiusuo.com/article/${article.slug}`,
    },
    keywords: article.tags.join(', '),
    articleSection: category?.name || '',
    wordCount: article.content.length,
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={article.title}
        description={article.excerpt}
        keywords={`不良研究所,${article.tags.join(',')},深度研究内容,独立创意研究`}
        canonicalUrl={`https://buliangyanjiusuo.com/article/${article.slug}`}
        ogImage={article.imageUrl}
        ogType="article"
        articlePublishedTime={article.publishDate}
        articleAuthor={article.author}
        articleSection={category?.name}
        articleTags={article.tags}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <article className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(category
                ? [{ label: category.name, href: `/category/${category.slug}` }]
                : []),
              { label: article.title },
            ]}
          />
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-6 pb-6">
          <div className="aspect-[16/8] rounded-lg overflow-hidden mb-8">
            <img
              src={article.imageUrl}
              alt={article.imageAlt}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs font-medium text-primary-600 bg-primary-100/70 px-2.5 py-1 rounded-full">
              {article.tags[0]}
            </span>
            {article.tags.slice(1).map((tag) => (
              <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2.5 py-1 rounded-full">
                {tag}
              </span>
            ))}
          </div>

          <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4"
            style={{ fontFamily: 'var(--font-heading)' }}>
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200/70">
            <span className="text-foreground-900 font-medium">{article.author}</span>
            <span>·</span>
            <span>{article.publishDate}</span>
            <span>·</span>
            <span>{article.readTime}</span>
            {category && (
              <>
                <span>·</span>
                <Link
                  to={`/category/${category.slug}`}
                  className="text-primary-600 hover:text-primary-700"
                >
                  {category.name}
                </Link>
              </>
            )}
          </div>

          <div
            className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed
              prose-h2:text-xl md:prose-h2:text-2xl prose-h2:font-bold prose-h2:text-foreground-900 prose-h2:mt-10 prose-h2:mb-4
              prose-p:text-sm md:prose-p:text-base prose-p:leading-relaxed prose-p:mb-5 prose-p:text-foreground-700
              prose-strong:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex items-center gap-2 text-sm text-foreground-500">
              <span>作者：</span>
              <span className="text-foreground-900 font-medium">{article.author}</span>
              <span className="text-foreground-400">|</span>
              <span>{article.authorBio}</span>
            </div>
          </div>
        </div>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-colors duration-200"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={related.imageUrl}
                        alt={related.imageAlt}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mb-2">
                        {related.title}
                      </h3>
                      <p className="text-xs text-foreground-500">{related.publishDate} · {related.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </>
  );
}